import { useEffect, useId, useMemo, useState } from 'react';
import { ArrowDown, Clock, CurrencyEur } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { useTranslation } from '../../i18n/I18nContext';
import {
  SHOWCASE_FINANCE,
  aprFromNominal,
  calculateFinance,
  depositRange,
} from '../../utils/financeCalculator';
import { formatEuro, formatPercent } from '../../utils/formatMoney';
import './FinanceCalculator.css';

/**
 * Витринный калькулятор платежа — раздел «Финансирование» страницы авто
 * (#1335, вариант C с холста).
 *
 * Обычный кредит: сумма кредита = цена − взнос, срок на выбор, фиксированная
 * демо-ставка. Слева то, что двигаешь (взнос и срок ползунками с иконкой),
 * справа то, что получаешь (платёж крупно, кредит, переплата, итого и кнопка
 * заявки). В колонке цены его заменяет `FinanceTeaser` — он ведёт сюда.
 *
 * Только витрина: страница рендерит его под флагом `SHOW_FINANCE_CALCULATOR`,
 * которого у дилерских виджетов нет. BF-пакет дилеров — отдельная ветка
 * `CarDetailPage`, её этот компонент не касается (#1259).
 *
 * @param {object} props
 * @param {number} props.price — цена авто, > 0
 * @param {(quote: ReturnType<typeof calculateFinance>) => void} [props.onRequestOffer]
 *   открыть заявку на финансирование; получает текущий расчёт, чтобы в CRM
 *   ушли те цифры, что видит покупатель
 * @param {(quote: ReturnType<typeof calculateFinance>) => void} [props.onQuoteChange]
 *   текущий расчёт наружу — чтобы тизер показывал те же цифры
 * @param {typeof SHOWCASE_FINANCE} [props.options] — ставка, сроки, шаг
 */
export function FinanceCalculator({
  price,
  onRequestOffer,
  onQuoteChange,
  options = SHOWCASE_FINANCE,
}) {
  const { t, language } = useTranslation();
  const ids = useId();
  const titleId = `${ids}-title`;
  const depositId = `${ids}-deposit`;
  const termId = `${ids}-term`;

  const range = useMemo(() => depositRange(price, options), [price, options]);
  const [months, setMonths] = useState(options.defaultTerm);
  const [deposit, setDeposit] = useState(range.initial);

  // Другая машина — другой диапазон: старый взнос мог в него не попасть.
  useEffect(() => {
    setDeposit(range.initial);
  }, [range.initial]);

  const result = calculateFinance({ price, deposit, months }, options);

  useEffect(() => {
    onQuoteChange?.(result);
    // Сообщаем только при смене цифр, а не каждого объекта расчёта.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.monthly, result.deposit, result.months]);

  const money = (value) => formatEuro(value, language);
  const rate = formatPercent(options.annualRate, language);
  // Эффективная годовая ставка (APR / TAEG). Рядом с платежом её требуют и
  // CCD (ЕС), и FCA.
  const apr = formatPercent(aprFromNominal(options.annualRate), language);
  const monthsText = `${result.months} ${t('showcase.car.finance.months', 'months')}`;

  const termMin = options.terms[0];
  const termMax = options.terms[options.terms.length - 1];
  const termStep =
    options.terms.length > 1 ? options.terms[1] - options.terms[0] : 1;

  const clamp = (value) => Math.min(Math.max(value, range.min), range.max);

  return (
    <section className="finance-calculator" aria-labelledby={titleId}>
      <div className="finance-calculator__controls">
        <div className="finance-calculator__field">
          <div className="finance-calculator__field-head">
            <label htmlFor={depositId} className="finance-calculator__label">
              {t('showcase.car.finance.downPayment', 'Down payment')}
            </label>
            <span className="finance-calculator__value">{money(result.deposit)}</span>
          </div>
          <Slider
            id={depositId}
            min={range.min}
            max={range.max}
            step={options.depositStep}
            value={result.deposit}
            onChange={(event) =>
              setDeposit(clamp(Math.round(Number(event.target.value))))
            }
            aria-valuetext={money(result.deposit)}
            disabled={range.max === 0}
            thumbIcon={<CurrencyEur size={16} weight="bold" />}
          />
        </div>

        <div className="finance-calculator__field">
          <div className="finance-calculator__field-head">
            <label htmlFor={termId} className="finance-calculator__label">
              {t('showcase.car.finance.term', 'Term')}
            </label>
            <span className="finance-calculator__value">{monthsText}</span>
          </div>
          <Slider
            id={termId}
            min={termMin}
            max={termMax}
            step={termStep}
            value={result.months}
            onChange={(event) => setMonths(Number(event.target.value))}
            aria-valuetext={monthsText}
            thumbIcon={<Clock size={16} weight="bold" />}
          />
        </div>

        <p className="finance-calculator__rate">
          {t('showcase.car.finance.rate', 'Fixed borrowing rate')} {rate}{' '}
          {t('showcase.car.finance.perYear', 'p.a.')} ·{' '}
          {t('showcase.car.finance.apr', 'APR')} {apr}
        </p>
      </div>

      <div className="finance-calculator__result">
        <div className="finance-calculator__head">
          <span className="finance-calculator__eyebrow" id={titleId}>
            {t('showcase.car.finance.title', 'Monthly payment')}
          </span>
          <p className="finance-calculator__amount finance-calculator__amount--hero">
            {money(result.monthly)}
            <span className="finance-calculator__per">
              {t('showcase.car.finance.perMonth', '/mo')}
            </span>
          </p>
        </div>
        <dl className="finance-calculator__breakdown">
          <div className="finance-calculator__row">
            <dt>{t('showcase.car.finance.loanAmount', 'Loan amount')}</dt>
            <dd>{money(result.loan)}</dd>
          </div>
          <div className="finance-calculator__row">
            <dt>{t('showcase.car.finance.totalInterest', 'Total interest')}</dt>
            <dd>{money(result.totalInterest)}</dd>
          </div>
          <div className="finance-calculator__row finance-calculator__row--total">
            <dt>{t('showcase.car.finance.totalPayable', 'Total payable')}</dt>
            <dd>{money(result.totalPayable)}</dd>
          </div>
        </dl>
        {onRequestOffer && (
          <Button
            size="lg"
            // Чёрная: синее на странице уже у главного действия в колонке
            // цены («Check availability»).
            variant="dark"
            className="finance-calculator__cta"
            onClick={() => onRequestOffer(result)}
          >
            {t('showcase.car.finance.cta', 'Request a finance offer')}
          </Button>
        )}
      </div>
    </section>
  );
}

/**
 * Тизер платежа для колонки цены (#1335): платёж и APR рядом с ценой, по
 * клику — переход к разделу «Финансирование» ниже на странице. Полный
 * калькулятор в узкой колонке растягивал её на два экрана, а платёж и APR
 * рядом с ценой — то, что покупатель ищет первым.
 *
 * Цифры приходят от калькулятора (`quote`): тизер не считает сам, чтобы не
 * показать другой платёж, чем в разделе ниже.
 *
 * @param {object} props
 * @param {ReturnType<typeof calculateFinance>} props.quote
 * @param {string} props.href — якорь раздела, `#…`
 * @param {(event: import('react').MouseEvent) => void} [props.onClick]
 * @param {typeof SHOWCASE_FINANCE} [props.options]
 */
export function FinanceTeaser({ quote, href, onClick, options = SHOWCASE_FINANCE }) {
  const { t, language } = useTranslation();
  const money = (value) => formatEuro(value, language);
  const apr = formatPercent(aprFromNominal(options.annualRate), language);

  return (
    <a className="finance-teaser" href={href} onClick={onClick}>
      <span className="finance-teaser__eyebrow">
        {t('showcase.car.finance.title', 'Monthly payment')}
      </span>
      <span className="finance-teaser__amount">
        {money(quote.monthly)}
        <span className="finance-calculator__per">
          {t('showcase.car.finance.perMonth', '/mo')}
        </span>
        {/* APR — того же кегля, что платёж: правило CCD/FCA «не менее
            заметно». */}
        <span className="finance-teaser__apr">
          {apr}
          <span className="finance-calculator__per">
            {t('showcase.car.finance.apr', 'APR')}
          </span>
        </span>
      </span>
      <span className="finance-teaser__action">
        {t('showcase.car.finance.show', 'Calculate')}
        {/* Круг со стрелкой — тот же, что у кнопок `arrow` (`.btn__arrow`),
            только вниз: тизер ведёт к разделу ниже на странице. */}
        <span className="btn__arrow" aria-hidden="true">
          <ArrowDown size={16} weight="bold" />
        </span>
      </span>
    </a>
  );
}

export default FinanceCalculator;
